import React from 'react';
import { MapPin, Check, Crown } from 'lucide-react';

interface GymMembershipProps {
  membership: {
    id: number;
    name: string;
    description: string;
    location: string;
    originalPrice: number;
    discountedPrice: number;
    image: string;
    features: string[];
    duration: string;
    popular?: boolean;
  };
}

export function GymMembershipCard({ membership }: GymMembershipProps) {
  const discount = Math.round(((membership.originalPrice - membership.discountedPrice) / membership.originalPrice) * 100);

  return (
    <div className="theme-card group relative overflow-hidden">
      {/* Image Section */}
      <div className="h-48 overflow-hidden">
        <img
          src={membership.image}
          alt={membership.name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => {
            e.currentTarget.src = 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&q=80';
          }}
        />
        <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
          {discount}% OFF
        </div>
        {membership.popular && (
          <div className="absolute top-4 left-4 bg-warning/20 text-warning px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
            <Crown className="w-4 h-4" />
            Popular
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-4 space-y-4">
        <div>
          <h3 className="text-lg font-semibold mb-1">{membership.name}</h3>
          <p className="text-sm text-muted-foreground line-clamp-2">{membership.description}</p>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <MapPin className="w-4 h-4" />
          <span>{membership.location}</span>
        </div>

        {/* Features */}
        <div className="space-y-2">
          {membership.features.map((feature, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center">
                <Check className="w-3 h-3 text-primary" />
              </div>
              <span>{feature}</span>
            </div>
          ))}
        </div>

        {/* Price and Duration */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-muted-foreground">{membership.duration}</div>
            <div className="text-right">
              <div className="text-sm line-through text-muted-foreground">
                ₹{membership.originalPrice.toLocaleString()}
              </div>
              <div className="text-lg font-bold text-primary">
                ₹{membership.discountedPrice.toLocaleString()}
              </div>
            </div>
          </div>
          <button className="theme-button w-full py-2 rounded-lg text-sm">
            Get Membership
          </button>
        </div>
      </div>
    </div>
  );
}